import React from "react";
import { useNavigate } from "react-router-dom";
import useAdminUsers from "./hooks/useAdminUsers";

export function AdminUserTable({ users, onDelete, onMakeAdmin, onRemoveAdmin, onChangeUserType, findRegionById }) {
  if (!users || users.length === 0) {
    return <p className="mb-6 text-gray-600">No data available at the moment.</p>;
  }

  const resolveRegion = typeof findRegionById === "function" ? findRegionById : () => null;

  const toArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return [];
      try {
        const candidate = JSON.parse(trimmed);
        if (Array.isArray(candidate)) return candidate;
        if (candidate && typeof candidate === "object") return [candidate];
      } catch (error) {
        // ignore parse errors and fall back to comma split
      }
      return trimmed.split(",").map((entry) => entry.trim()).filter(Boolean);
    }
    return [value];
  };

  const buildLabel = (region) => {
    if (!region) return null;
    const city = (region.city || region.name || "").toString().trim();
    const state = (region.state || region.region || "").toString().trim();
    const parts = [city, state].filter(Boolean);
    if (parts.length) return parts.join(", ");
    if (region._id != null) {
      const fallback = region._id.toString().trim();
      if (fallback) return fallback;
    }
    return null;
  };

  const formatRegions = (regions) => {
    const list = toArray(regions);
    if (!list.length) return "—";

    const labels = list
      .map((entry) => {
        if (!entry) return null;

        if (typeof entry === "string" || typeof entry === "number") {
          const match = resolveRegion(entry);
          if (match) return buildLabel(match);
          const fallback = entry.toString().trim();
          return fallback || null;
        }

        if (typeof entry === "object") {
          const ids = [entry._id, entry.id, entry.value]
            .map((candidate) => (candidate == null ? null : candidate.toString().trim()))
            .filter(Boolean);

          for (const candidate of ids) {
            const match = resolveRegion(candidate);
            if (match) return buildLabel(match);
          }

          const label = buildLabel(entry);
          if (label) return label;

          if (ids.length) {
            const [first] = ids;
            if (first) return first;
          }

          const city = (entry.cityId || entry.city || "").toString().trim();
          if (city) return city;

          return null;
        }

        return null;
      })
      .filter(Boolean);

    return labels.length ? labels.join(" | ") : "—";
  };

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-4">User List</h2>
      <table className="min-w-full bg-white border border-gray-200">
        <thead>
          <tr className="w-full bg-gray-100 text-gray-700 uppercase text-sm leading-normal">
            <th className="py-3 px-6 text-left">Full Name</th>
            <th className="py-3 px-6 text-left">Email</th>
            <th className="py-3 px-6 text-left">User Type</th>
            <th className="py-3 px-6 text-left">Organization</th>
            <th className="py-3 px-6 text-left">Job Title</th>
            <th className="py-3 px-6 text-left">Phone Number</th>
            <th className="py-3 px-6 text-left">Regions</th>
            <th className="py-3 px-6 text-left">Actions</th>
          </tr>
        </thead>
        <tbody className="text-gray-600 text-sm font-light">
          {users.map((user) => {
            const uid = user._id || user.id;
            return (
              <tr key={uid} className="border-b border-gray-200 hover:bg-gray-100">
                <td className="py-3 px-6">{user.fullName || "—"}</td>
                <td className="py-3 px-6">{user.email || "—"}</td>
                <td className="py-3 px-6">{user.userType || "—"}</td>
                <td className="py-3 px-6">{user.organization || "—"}</td>
                <td className="py-3 px-6">{user.jobTitle || "—"}</td>
                <td className="py-3 px-6">{user.phoneNumber || "—"}</td>
                <td className="py-3 px-6">{formatRegions(user.regions)}</td>
                <td className="py-3 px-6">
                  <div className="flex items-center">
                    <button onClick={() => uid && onDelete(uid)} className="text-red-600 hover:text-red-800">Delete</button>
                    <select
                      value={user.userType}
                      onChange={(e) => uid && onChangeUserType(uid, e.target.value)}
                      className="ml-4 border rounded px-2 py-1 text-sm"
                      aria-label={`Change user type for ${user.fullName}`}>
                      <option value="admin">admin</option>
                      <option value="regularuser">regularuser</option>
                      <option value="deactivated">deactivated</option>
                    </select>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const AdminUserList = () => {
  const {
    users,
    loading,
    error,
    removeUser,
    setAdmin,
    removeAdmin,
    setUserType,
    findRegionById,
  } = useAdminUsers();
  const navigate = useNavigate();
  const userType = localStorage.getItem("userType");
  const isAdmin = userType === "admin";

  if (!isAdmin) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
        <p>You do not have permission to view this page.</p>
        <button className="mt-4 px-4 py-2 bg-blue-600 text-white rounded" onClick={() => navigate("/")}>Go Home</button>
      </div>
    );
  }

  if (loading) return (
    <div className="p-4 text-center">
      <h1 className="text-3xl font-bold mb-4">Admin Dashboard</h1>
      <p className="mb-6 text-gray-600">Loading data, please wait...</p>
    </div>
  );
  if (error) return (
    <div className="p-4 text-center">
      <h1 className="text-3xl font-bold mb-4">Admin Dashboard</h1>
      <p className="mb-6 text-gray-600">{error}</p>
    </div>
  );

  return (
    <div className="p-4 text-center font-['corporate-s'] text-left">
      <h1 className="text-3xl font-bold mb-4">Admin — User List</h1>
  <AdminUserTable users={users} onDelete={removeUser} onMakeAdmin={setAdmin} onRemoveAdmin={removeAdmin} onChangeUserType={setUserType} findRegionById={findRegionById} />
    </div>
  );
};

export default AdminUserList;